import { FileSpreadsheet } from "lucide-react";
import { useRef, useState } from "react";
import { REPORT_MEDIA_LINKS_TEMPLATE_URL } from "../lib/constants";
import styles from "../styles.module.scss";

interface UploadStepProps {
  file: File | null;
  isBusy: boolean;
  onFileSelect: (file: File) => void;
}

export function UploadStep({ file, isBusy, onFileSelect }: UploadStepProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFileSelect(dropped);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) onFileSelect(selected);
  };

  return (
    <div className={styles.content}>
      <p className={styles.description}>
        Upload a CSV containing media links for this report.
        <br />
        <a
          href={REPORT_MEDIA_LINKS_TEMPLATE_URL}
          target="_blank"
          rel="noreferrer"
          className={styles.templateLink}
        >
          Download template
        </a>
      </p>

      <div
        className={`${styles.dropZone} ${
          isDragging ? styles.dropZoneActive : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={styles.dropZoneIcon}>
          <FileSpreadsheet size={32} />
        </div>
        <h4 className={styles.dropZoneTitle}>Drag and drop your file here</h4>
        <p className={styles.dropZoneSubtitle}>Supports .csv</p>
        <label className={styles.browseButton}>
          Browse Files
          <input
            type="file"
            ref={fileInputRef}
            className={styles.hiddenInput}
            accept=".csv"
            onChange={handleInputChange}
            disabled={isBusy}
          />
        </label>
      </div>

      {file && (
        <div className={styles.fileInfo}>
          <FileSpreadsheet size={16} />
          <span className={styles.fileName}>{file.name}</span>
          {isBusy && (
            <span className={styles.uploadingText}>Validating...</span>
          )}
        </div>
      )}
    </div>
  );
}
